<template>
    <AuthHeader />
    <div class="container">
        <h1 class="my-3">{{ $t('login.title') }}</h1>
        <div class="row my-3">
            <div class="col d-grid">
                <button class="btn btn-lg border-1 border-primary-subtle" @click="loginWithMicrosoft">
                    <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21"
                        class="mssignin-logo">
                        <title>MS-SymbolLockup</title>
                        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                    </svg>
                    <span class="mssignin-label">{{ $t('login.withMicrosoft') }}</span>
                </button>
            </div>
        </div>
        <div class="separator"><small>{{ $t('login.or') }}</small></div>

        <form @submit.prevent="login">
            <div class="row mt-3">
                <div class="col">
                    <label for="username" class="small">{{ $t('login.username') }}</label>
                    <input v-model="username" type="text" class="form-control form-control-lg mt-2 fs-6 p-3"
                        id="username" :placeholder="$t('login.username')" required>
                </div>
            </div>
            <div class="row mt-3">
                <div class="col">
                    <label for="password" class="small">{{ $t('login.password') }}</label>
                    <input v-model="password" type="password" class="form-control form-control-lg mt-2 fs-6 p-3"
                        id="password" :placeholder="$t('login.password')" required>
                </div>
            </div>
            <div class="row mt-3">
                <div class="col d-grid">
                    <button type="submit" class="btn btn-lg btn-primary">{{ $t('login.signIn') }}</button>
                </div>
            </div>
            <div v-if="loginFailed" class="text-center mt-3">
                <router-link to="/forgot-password" class="small">{{ $t('login.forgotPassword') }}</router-link>
            </div>
        </form>
    </div>
</template>

<script>
import axios from '@/services/axios';
import { msalInstance, initializeMsal } from '@/composables/msalConfig';
import { setPermsFromToken } from '@/composables/usePerms';
import { setActAsFromLogin, clearActAs } from '@/composables/useActAs';
import { loadTemplates } from '@/composables/useTextTemplates';
import { setLocaleFromUser } from '@/i18n';
import AuthHeader from '@/components/AuthHeader.vue';

export default {
    components: {
        AuthHeader
    },
    data() {
        return {
            username: '',
            password: '',
            // Set after wrong credentials, to offer the forgot-password link.
            loginFailed: false,
        };
    },
    methods: {
        redirectAfterLogin() {
            const target = this.$route.query.redirect;
            // only allow internal, absolute paths; never loop back to /login
            if (typeof target === 'string' && target.startsWith('/') && !target.startsWith('//') && !target.startsWith('/login')) {
                this.$router.replace(target);
            } else {
                this.$router.replace('/');
            }
        },
        async login() {
            try {
                const response = await axios.post('login/', {
                    username: this.username,
                    password: this.password
                });
                const { token, user } = response.data.data;
                localStorage.setItem('token', token);
                localStorage.setItem('loginMethod', 'standard');
                localStorage.setItem('user', JSON.stringify(user));

                setPermsFromToken(token);
                setActAsFromLogin(response.data.data);
                setLocaleFromUser(user);
                // Unawaited: the quick-pick button just stays hidden until it lands.
                loadTemplates(true);

                this.redirectAfterLogin();
            } catch (error) {
                if (error.response?.status === 401) {
                    this.loginFailed = true;
                }
                this.$root.$refs.globalAlert.showAlert(this.$t('login.error'), 'danger');
                console.error("Login error", error);
            }
        },
        async loginWithMicrosoft() {
            try {
                const loginRequest = {
                    scopes: ["User.Read", "offline_access"],
                    prompt: "select_account"
                };
                const response = await msalInstance.loginPopup(loginRequest);
                this.sendTokenToApi(response.accessToken);
            } catch (error) {
                this.$root.$refs.globalAlert.showAlert(this.$t('login.microsoftError'), 'danger');
                console.error("Login with Microsoft error", error);
            }
        },

        async sendTokenToApi(token) {
            try {
                const response = await axios.post('login/microsoft', { token });
                const { token: apiToken, user } = response.data.data;
                localStorage.setItem('token', apiToken);
                localStorage.setItem('loginMethod', 'microsoft');
                localStorage.setItem('user', JSON.stringify(user));

                setPermsFromToken(apiToken);
                clearActAs();
                setLocaleFromUser(user);
                loadTemplates(true);

                this.redirectAfterLogin();
            } catch (error) {
                console.error("Error sending token to API", error);
            }
        }
    },
    created() {
        initializeMsal().then(() => {
        }).catch(error => {
            console.error('Error initializing MSAL', error);
        });
    },
};
</script>

<style scoped>
.mssignin-button {
    display: inline-flex;
    align-items: center;
    border: 1px solid var(--bs-border-color);
    background: var(--bs-body-bg);
    height: 41px;
    padding: 16px;
    cursor: pointer;
    width: 100%;
    border-radius: 40px;
}

.mssignin-logo {
    margin-right: 10px;
}

.mssignin-label {
    color: var(--bs-body-color);
    font-family: "Segoe UI", Frutiger, "Frutiger Linotype", "Dejavu Sans", "Helvetica Neue", Arial, sans-serif;
    font-size: 15px;
    font-weight: 600;
}

.separator {
    display: flex;
    align-items: center;
    text-align: center;
}

.separator::before,
.separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--bs-border-color);
}

.separator:not(:empty)::before {
    margin-right: 1em;
}

.separator:not(:empty)::after {
    margin-left: 1em;
}
</style>